import { useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as z from "zod";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { Permission, useRolePermissions } from "@/hooks/use-role-permissions";
import { buildOrgPath } from "@/common/utils/orgRouteUtils";
import { BillingContactCard } from "@/features/organization-billing/billing-contact-card";
import { PaymentMethodCard } from "@/features/organization-billing/payment-method-card";
import { UsageCard } from "@/features/organization-billing/usage-card";
import { InvoicesCard } from "@/features/organization-billing/invoices-card";

const REDIRECT_STATUS_SUCCEEDED = "succeeded";

// Stripe appends these when a payment method needs authentication and returns here.
const billingSearchSchema = z.object({
  setup_intent: z.string().optional(),
  setup_intent_client_secret: z.string().optional(),
  redirect_status: z.string().optional(),
  billing_account_version: z.coerce.number().int().positive().optional(),
});

function BillingPage() {
  const { user } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { selectedOrgBusinessUnit } = useOrganization();
  const organizationBusinessUnitId = selectedOrgBusinessUnit.id;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { hasPermission } = useRolePermissions(user);
  const canView = hasPermission(Permission.ViewInvoices);
  const canManage = hasPermission(Permission.ManageInvoices);

  const summaryQuery = useQuery({
    ...trpc.organization.getBillingAccount.queryOptions({
      organizationBusinessUnitId,
    }),
    enabled: canView,
  });

  const confirmMutation = useMutation(
    trpc.organization.confirmDefaultPaymentMethod.mutationOptions({
      onSuccess: async () => {
        toast.success("Payment method saved");
        await queryClient.invalidateQueries(
          trpc.organization.getBillingAccount.queryFilter({
            organizationBusinessUnitId,
          }),
        );
      },
      onError: (error) => {
        toast.error("Couldn't save the payment method", {
          description: error.message,
        });
      },
    }),
  );

  const handledRedirectRef = useRef(false);
  const {
    setup_intent: setupIntentId,
    redirect_status: redirectStatus,
    billing_account_version: expectedVersion,
  } = search;
  useEffect(() => {
    if (!setupIntentId || handledRedirectRef.current) {
      return;
    }
    handledRedirectRef.current = true;
    if (!expectedVersion) {
      toast.error("The billing account changed. Start payment setup again.");
      void navigate({ search: () => ({}), replace: true });
      return;
    }
    if (redirectStatus === REDIRECT_STATUS_SUCCEEDED) {
      // Keep the redirect params until the server confirms the payment method.
      // Only clear them on success; leave them on failure so a page refresh
      // can replay this idempotent confirmation.
      confirmMutation.mutate(
        { organizationBusinessUnitId, setupIntentId, expectedVersion },
        {
          onSuccess: () => {
            void navigate({ search: () => ({}), replace: true });
          },
        },
      );
    } else {
      // Non-succeeded statuses (auth failure, cancellation, etc.) cannot be
      // retried, so it is safe to clear the params after showing the error.
      toast.error("The payment method could not be saved");
      void navigate({ search: () => ({}), replace: true });
    }
  }, [
    setupIntentId,
    redirectStatus,
    expectedVersion,
    organizationBusinessUnitId,
    confirmMutation,
    navigate,
  ]);

  const returnUrl = `${window.location.origin}${buildOrgPath(
    organizationBusinessUnitId,
    "/billing",
  )}`;

  return (
    <div className="p-2 pt-4 md:pt-8 md:p-8 flex justify-center">
      <div className="space-y-6 max-w-3xl w-full">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-sm text-muted-foreground">
            Billing contact, payment method, usage, and invoices for{" "}
            {selectedOrgBusinessUnit.name}.
          </p>
        </div>

        {!canView ? (
          <Card className="p-4">
            <div className="text-center">
              You do not have permission to view billing.
            </div>
          </Card>
        ) : summaryQuery.isLoading || !summaryQuery.data ? (
          <div className="space-y-6" role="status">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <>
            <BillingContactCard
              key={summaryQuery.data.account?.version ?? "new"}
              organizationBusinessUnitId={organizationBusinessUnitId}
              summary={summaryQuery.data}
              canManage={canManage}
            />
            <PaymentMethodCard
              organizationBusinessUnitId={organizationBusinessUnitId}
              summary={summaryQuery.data}
              canManage={canManage}
              returnUrl={returnUrl}
            />
            <UsageCard
              organizationBusinessUnitId={organizationBusinessUnitId}
              unitName={selectedOrgBusinessUnit.name}
            />
            <InvoicesCard
              organizationBusinessUnitId={organizationBusinessUnitId}
              unitName={selectedOrgBusinessUnit.name}
              canManage={canManage}
              account={
                summaryQuery.data.account
                  ? {
                      version: summaryQuery.data.account.version,
                      paymentMethod:
                        summaryQuery.data.account.defaultPaymentMethod,
                    }
                  : null
              }
            />
          </>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/billing/",
)({
  validateSearch: billingSearchSchema,
  component: BillingPage,
});
